import { useCallback, useEffect, useState } from "react";
import { Plus, Search, Pencil, Trash2, Download } from "lucide-react";
import { api, errMsg, downloadAuth } from "@/lib/api";
import { FormGrid } from "./Fields";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";

export const get = (o, path) => path.split(".").reduce((a, k) => (a == null ? a : a[k]), o);

export function PageTitle({ title, subtitle, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div><h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">{title}</h1>{subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}</div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

export const Btn = ({ children, variant = "primary", className = "", ...p }) => (
  <button {...p} className={`inline-flex items-center gap-2 rounded-full text-sm font-semibold px-4 py-2 transition-colors disabled:opacity-50 ${variant === "primary" ? "bg-[#FF3333] hover:bg-[#E62E2E] text-white" : "bg-white border border-slate-200 hover:border-slate-400"} ${className}`}>{children}</button>
);

export function ExportButtons({ name, params = "" }) {
  return ["csv", "xlsx", "pdf"].map((f) => (
    <Btn key={f} variant="ghost" onClick={() => downloadAuth(`/admin/export/${name}?format=${f}${params}`, `${name}.${f}`).catch(() => toast.error("No se pudo exportar"))} data-testid={`export-${name}-${f}`}>
      <Download size={14} />{f.toUpperCase()}
    </Btn>
  ));
}

export default function ResourcePage({ name, title, subtitle, fields, columns, fixed = {}, defaults = {}, rowActions, extra, onSaved, hideHeader, exportable = true, reloadKey, toolbar, sort }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const fixedKey = JSON.stringify(fixed);

  const load = useCallback(() => {
    api.get(`/admin/c/${name}`, { params: { q, ...(sort ? { sort } : {}), ...JSON.parse(fixedKey) } }).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [name, q, fixedKey, sort]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load, reloadKey]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = edit.id ? await api.put(`/admin/c/${name}/${edit.id}`, edit) : await api.post(`/admin/c/${name}`, edit);
      toast.success("Guardado");
      setEdit(null);
      load();
      onSaved && onSaved(r.data);
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };
  const remove = async (row) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    try { await api.delete(`/admin/c/${name}/${row.id}`); toast.success("Eliminado"); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div data-testid={`resource-${name}`}>
      {!hideHeader && <PageTitle title={title} subtitle={subtitle}>{exportable && <ExportButtons name={name} />}</PageTitle>}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9 bg-white" placeholder="Buscar..." value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${name}-search-input`} /></div>
        <Btn onClick={() => setEdit({ ...defaults, ...fixed })} data-testid={`${name}-new-button`}><Plus size={15} />Nuevo</Btn>
        {toolbar}
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>{columns.map((c) => <th key={c.key} className="px-4 py-3 font-semibold whitespace-nowrap">{c.label}</th>)}<th className="px-4 py-3" /></tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`${name}-row-${row.id}`}>
                {columns.map((c) => <td key={c.key} className="px-4 py-3 whitespace-nowrap">{c.render ? c.render(row) : (get(row, c.key) ?? "—").toString()}</td>)}
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {rowActions && rowActions(row, load)}
                  <button onClick={() => setEdit(row)} className="p-1.5 hover:text-[#FF3333]" data-testid={`${name}-edit-${row.id}`}><Pencil size={15} /></button>
                  <button onClick={() => remove(row)} className="p-1.5 hover:text-red-600" data-testid={`${name}-delete-${row.id}`}><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-slate-400" data-testid={`${name}-empty`}>Sin registros</td></tr>}
          </tbody>
        </table>
      </div>
      <Sheet open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader><SheetTitle>{edit?.id ? "Editar" : "Nuevo"} — {title}</SheetTitle></SheetHeader>
          {edit && (
            <form onSubmit={save} className="space-y-6 mt-6" data-testid={`${name}-form`}>
              <FormGrid fields={fields} value={edit} onChange={setEdit} prefix={name} />
              {extra && edit.id && extra(edit, setEdit)}
              <div className="flex gap-3 sticky bottom-0 z-10 bg-white py-4 border-t">
                <Btn type="submit" disabled={saving} data-testid={`${name}-save-button`}>{saving ? "Guardando..." : "Guardar"}</Btn>
                <Btn type="button" variant="ghost" onClick={() => setEdit(null)}>Cancelar</Btn>
              </div>
            </form>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
